import { Injectable } from '@angular/core'
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling'
import { Observable, Subject, fromEvent } from 'rxjs'
import { map, throttleTime, distinctUntilChanged } from 'rxjs/operators'

@Injectable({
  providedIn: 'root'
})
export class ScrollDirectionService {
  private scrollDirection: 'up' | 'down' = 'down'
  private prevScrollY = 0
  private scrollingThreshold = 10 // Reduced threshold for better responsiveness
  private scrollSubject = new Subject<boolean>()
  private isUserScrolling = false // Track if user is actually scrolling

  constructor(private scrollDispatcher: ScrollDispatcher) {
    this.setupScrollObservables()
  }

  getScrollDirection(): Observable<boolean> {
    return this.scrollSubject.asObservable().pipe(
      distinctUntilChanged() // Only emit when direction actually changes
    )
  }

  private onWindowScroll = () => {
    // Ignore scroll events when they might be programmatic (like overlay opening)
    if (!this.isUserScrolling) {
      this.isUserScrolling = true
      setTimeout(() => {
        this.isUserScrolling = false
      }, 150) // Reset after 150ms
      return
    }

    const scrollY = window.scrollY

    // Only process if we've scrolled past the threshold
    const scrollDiff = scrollY - this.prevScrollY

    if (Math.abs(scrollDiff) < this.scrollingThreshold) {
      return // Not enough scroll to matter
    }

    if (scrollDiff > 0) {
      // Scrolling DOWN - hide toolbar
      if (this.scrollDirection !== 'down') {
        this.scrollDirection = 'down'
        this.scrollSubject.next(true) // true = scrolling down (hide)
      }
    } else {
      // Scrolling UP - show toolbar
      if (this.scrollDirection !== 'up') {
        this.scrollDirection = 'up'
        this.scrollSubject.next(false) // false = scrolling up (show)
      }
    }

    this.prevScrollY = scrollY
  }

  private setupScrollObservables() {
    // Use RxJS fromEvent instead of addEventListener for better control
    fromEvent(window, 'scroll')
      .pipe(
        throttleTime(16), // ~60fps throttling
        map(() => this.onWindowScroll())
      )
      .subscribe()

    // Handle CDK scrollable containers (optional - remove if not needed)
    this.scrollDispatcher
      .scrolled()
      .pipe(
        throttleTime(16),
        map((cdk: CdkScrollable) => {
          if (cdk && this.isUserScrolling) {
            const containerOffset =
              cdk.getElementRef().nativeElement.scrollTop || 0
            const scrollDiff = containerOffset - this.prevScrollY

            if (Math.abs(scrollDiff) >= this.scrollingThreshold) {
              if (scrollDiff > 0 && this.scrollDirection !== 'down') {
                this.scrollDirection = 'down'
                this.scrollSubject.next(true) // scrolling down
              } else if (scrollDiff < 0 && this.scrollDirection !== 'up') {
                this.scrollDirection = 'up'
                this.scrollSubject.next(false) // scrolling up
              }
              this.prevScrollY = containerOffset
            }
          }
        })
      )
      .subscribe()
  }

  // Call this method when overlays open/close to prevent unwanted triggers
  public resetScrollState(): void {
    this.isUserScrolling = false
    // Optionally reset to visible state when overlays open
    this.scrollSubject.next(false) // Show toolbar
  }
}
